import { DownOutlined, LockFilled, SearchOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Affix, Button, Dropdown, Input } from 'antd';
import React, { useState } from 'react';

import TranslateText from 'components/hoc/TranslateText';
import LanguageSelect from 'components/molecules/LanguageSelect';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { ETypZadosti } from 'enums/lei/leiTypZadosti';
import routePathEnum from 'enums/routePathEnum';

// height of the sticky header + a little breathing room
const HEADER_OFFSET = 100;

interface ILeiLoginLessMenu {}

const LeiLoginlessMenu: React.FC<ILeiLoginLessMenu> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const [query, setQuery] = useState('');

    const runSearch = () => {
        const val = query.trim();
        if (!val) return;
        // the search page detects whether the text is an LEI, an IČO or a company name
        navigateWorkaround(`${routePathEnum.LEI_LOGINLESS_SEARCH}?q=${encodeURIComponent(val)}`);
    };

    const scrollToId = (id: string) => {
        // look the element up inside the timeout so it also works right after a route change
        setTimeout(() => {
            const element = document.getElementById(id);
            if (!element) return;
            window.scrollTo({
                top: element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET,
                behavior: 'smooth'
            });
        }, 150);
    };

    const goTo = (id: string, typZadosti?: ETypZadosti) => {
        if (window.location.href.includes('lei-prehled-uctu') || typZadosti) {
            navigateWorkaround(`/lei-cdcp/${typZadosti || ''}`);
        }
        scrollToId(id);
    };

    const serviceItems: MenuProps['items'] = [
        {
            key: 'leiRegistrace',
            label: 'Nové LEI',
            onClick: () => goTo('leiRegistrace', ETypZadosti.REGISTRACE)
        },
        {
            key: 'leiAktualizace',
            label: 'Obnova LEI',
            onClick: () => goTo('leiAktualizace', ETypZadosti.AKTUALIZACE)
        },
        {
            key: 'leiPrevod',
            label: 'Převod LEI',
            onClick: () => goTo('leiPrevod', ETypZadosti.PREVOD)
        },
        {
            key: 'leiSeznam',
            label: 'Seznam přidělených LEI',
            onClick: () => navigateWorkaround('/lei-prehled-uctu/')
        },
        {
            key: 'leiHistorie',
            label: 'Historie vydaných LEI',
            onClick: () =>
                window.location.replace(
                    `https://${window.location.host}/lei/sluzby-lei/historie-vydanych-seznamu-lei-pre-lei/`
                )
        },
        {
            type: 'divider'
        },
        // former "Kód LEI" submenu - not in the mockup, kept here so the sections stay reachable
        {
            key: 'infoLei',
            label: 'Co je LEI?',
            onClick: () => navigateWorkaround(routePathEnum.LEI_LOGINLESS_INFO)
        },
        {
            key: 'Pravidla',
            label: 'Pravidla pro poskytování služby',
            onClick: () => goTo('Pravidla')
        }
    ];

    return (
        <Affix>
            <header className="lei-nav">
                <div className="lei-nav-inner">
                    <img
                        src="https://test.cdcp.cz/wp-content/uploads/dx_logo_barevne_rgb.svg"
                        alt="CDCP Logo"
                        className="lei-nav-logo"
                        onClick={() => navigateWorkaround('/lei-cdcp/')}
                    />

                    <nav className="lei-nav-links">
                        <Dropdown
                            menu={{
                                items: serviceItems
                            }}
                            trigger={['hover']}
                            placement="bottomLeft"
                        >
                            <button type="button" className="lei-nav-link">
                                LEI služby <DownOutlined />
                            </button>
                        </Dropdown>
                        <button type="button" className="lei-nav-link" onClick={() => goTo('Poplatky')}>
                            Ceník
                        </button>
                        <button
                            type="button"
                            className="lei-nav-link"
                            onClick={() => navigateWorkaround(routePathEnum.LEI_LOGINLESS_FAQ)}
                        >
                            FAQ
                        </button>
                        <button
                            type="button"
                            className="lei-nav-link"
                            onClick={() => navigateWorkaround(routePathEnum.LEI_LOGINLESS_ONAS)}
                        >
                            O nás
                        </button>
                        {/* TODO: point to the real contacts section once it has an id */}
                        <button type="button" className="lei-nav-link" onClick={() => goTo('Onas')}>
                            Kontakt
                        </button>
                    </nav>

                    <div className="lei-nav-actions">
                        <Input
                            className="lei-nav-search"
                            placeholder="Zadejte LEI, IČO nebo název společnosti"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onPressEnter={runSearch}
                            suffix={<SearchOutlined onClick={runSearch} />}
                        />
                        <Button
                            type="primary"
                            className="lei-nav-depozitar"
                            icon={<LockFilled />}
                            onClick={() =>
                                window.location.replace(`https://${window.location.host}/#/prihlaseni-rozcestnik`)
                            }
                        >
                            <TranslateText>MUJ_DEPOZITAR_LOWER</TranslateText>
                        </Button>
                        <LanguageSelect />
                    </div>
                </div>
            </header>
        </Affix>
    );
};

export default LeiLoginlessMenu;
